import React from "react";

import { IconButtonProps, SvgIconProps, tooltipClasses } from "@mui/material";
import styled from "@mui/material/styles/styled";

import { ELLIPSIS_SX } from "@app/common/styles";
import { PartialBy } from "@app/common/types";

import IconButton from "@rui/IconButton";
import Link from "@rui/Link";
import Stack, { Column, Row, StackProps } from "@rui/Stack";
import { TooltipWrapperProps } from "@rui/TooltipWrapper";
import TooltipWrapper from "@rui/TooltipWrapper";

export const Content = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(2),
  rowGap: theme.spacing(2),
  columnGap: theme.spacing(2),
}));

export const LeftColumn = styled(Column)(({ theme }) => ({
  width: "100%",
  rowGap: theme.spacing(2),
  [theme.breakpoints.up("lg")]: {
    width: "calc(70% - 16px)", // account for column gap
  },
}));

export const RightColumn = styled(Column)(({ theme }) => ({
  width: "100%",
  rowGap: theme.spacing(2),
  [theme.breakpoints.up("lg")]: {
    width: "30%",
  },
}));

export const SummaryModule = styled(Row)(({ theme }) => ({
  border: "solid 2px",
  borderColor: theme.palette.shades.primary[500],
  padding: theme.spacing(2),
  borderRadius: "8px",
  width: "100%",
  color: theme.palette.shades.neutral[800],
  boxShadow: "0px 2px 20px 0px rgba(0, 0, 0, 0.16)",
}));

type TextWithIconProps = StackProps & {
  Icon: React.JSXElementConstructor<SvgIconProps>;
  color?: React.CSSProperties["color"];
  href?: string;
  iconColor?: React.CSSProperties["color"];
};

export const TextWithIcon = styled(
  ({ Icon, children, href, iconColor: _, ...props }: TextWithIconProps) => (
    <Row variant="center" spacing={0.5} {...props}>
      <Icon />
      {href ? (
        <Link className="text-with-icon__content" href={href}>
          {children}
        </Link>
      ) : (
        <span className="text-with-icon__content">{children}</span>
      )}
    </Row>
  )
)(({ color, iconColor, theme }) => ({
  fontWeight: 500,
  fontSize: "0.75rem",
  fontFamily: theme.typography.fontFamily,
  overflow: "hidden",

  "& .text-with-icon__content": {
    color: color ?? theme.palette.shades.neutral[500],
    ...ELLIPSIS_SX,
  },

  "& a.text-with-icon__content": {
    color: "inherit",
    "&:hover": {
      color: theme.palette.shades.secondary[500],
      textDecoration: "underline",
    },
  },

  "& .MuiSvgIcon-root": {
    color: iconColor ?? color,
    fontSize: "inherit",
  },
}));

type SecondaryTextWithIconProps = PartialBy<TextWithIconProps, "color">;

export const SecondaryTextWithIcon = styled(
  (props: SecondaryTextWithIconProps) => (
    <TextWithIcon color="shades.neutral.400" iconColor="#C1C1C1" {...props} />
  )
)(({ theme }) => ({
  fontFamily: theme.typography.fontFamilySecondary,
}));

export const ModuleHeading = styled(Row)(({ theme }) => ({
  padding: theme.spacing(2),
  width: "100%",
}));

type ActionProps = IconButtonProps & {
  variant?: "primary" | "default" | "text";
  fontSize?: React.CSSProperties["fontSize"];
};

export const Action = styled(({ variant: _, ...props }: ActionProps) => (
  <IconButton {...props} />
))(({ fontSize = "16px", variant = "default", theme }) => ({
  height: "32px",
  width: "32px",
  backgroundColor:
    variant === "text"
      ? "transparent"
      : variant === "primary"
        ? theme.palette.shades.primary[500]
        : "#00000005",
  "& svg": {
    fontSize,
    "&:not(.copied-icon)": {
      color: variant === "primary" ? "#FFF" : theme.palette.shades.neutral[500],
    },
  },
  "&:hover": {
    backgroundColor:
      variant === "primary"
        ? theme.palette.shades.primary[600]
        : theme.palette.shades.primary[50],
    "& svg:not(.copied-icon)": {
      color: variant === "primary" ? "#FFF" : theme.palette.shades.primary[500],
    },
  },
}));

type CustomTooltipWrapperProps = TooltipWrapperProps & { minWidth?: number };

export const CustomTooltipWrapper = styled(
  ({ className, minWidth: _mw, ...props }: CustomTooltipWrapperProps) => (
    <TooltipWrapper arrow={false} {...props} classes={{ popper: className }} />
  )
)(({ minWidth = 300 }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#fff",
    minWidth,
    border: "1px solid #00000014",
    boxShadow: "0px 8px 24px 0px #0000001A",
  },
}));

export const TabsContainer = styled(Stack)(({ theme }) => ({
  backgroundColor: "#FFF",
  padding: theme.spacing(0, 2),
  borderBottom: `2px solid ${theme.palette.shades.neutral[50]}`,
  "& .MuiTabs-root": { marginBottom: "-2px" },
  position: "relative",
}));
